import {useEffect, useId, useRef, type KeyboardEvent, type ReactNode} from 'react';
import {createPortal} from 'react-dom';
import {X} from 'lucide-react';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface CallDialogProps {
    readonly title: string;
    readonly closeLabel?: string;
    readonly role?: 'dialog' | 'alertdialog';
    readonly width?: 'md' | 'sm';
    readonly testId?: string;
    readonly onClose: () => void;
    readonly children: ReactNode;
    readonly footer?: ReactNode;
}

export const CallDialog = ({title, closeLabel, role = 'dialog', width = 'md', testId, onClose, children, footer}: CallDialogProps) => {
    const panel = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const restoreFocus = useRef<Element | null>(null);

    useEffect(() => {
        restoreFocus.current = document.activeElement;
        panel.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
        return () => {
            if (restoreFocus.current instanceof HTMLElement) {
                restoreFocus.current.focus();
            }
        };
    }, []);

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            onClose();
            return;
        }
        if (event.key !== 'Tab' || !panel.current) {
            return;
        }
        const focusable = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
        }
    };

    return createPortal(
        <div className="phq-call-ui fixed inset-0 z-[80] flex items-center justify-center bg-[var(--phq-call-scrim)] p-4" onKeyDown={onKeyDown}>
            <div
                ref={panel}
                role={role}
                aria-modal="true"
                aria-labelledby={titleId}
                data-testid={testId}
                className={`phq-call-enter flex w-full flex-col gap-5 rounded-2xl bg-[var(--phq-call-surface)] p-6 shadow-[0_16px_48px_rgba(0,0,0,.6),inset_0_0_0_1px_var(--phq-call-line)] ${width === 'md' ? 'max-w-[560px]' : 'max-w-[440px]'}`}
            >
                <div className="flex items-start justify-between gap-4">
                    <h2 id={titleId} className="text-lg font-semibold leading-tight">{title}</h2>
                    {closeLabel && (
                        <button
                            type="button"
                            aria-label={closeLabel}
                            onClick={onClose}
                            className="phq-call-press -mr-1 -mt-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-[var(--phq-call-ink-muted)] hover:bg-[var(--phq-call-surface-muted)]"
                        >
                            <X size={18} aria-hidden="true"/>
                        </button>
                    )}
                </div>
                {children}
                {footer && <div className="flex justify-end gap-2">{footer}</div>}
            </div>
        </div>,
        document.body,
    );
};

export const DialogButton = ({tone, label, testId, disabled, onClick}: {tone: 'brand' | 'neutral'; label: string; testId?: string; disabled?: boolean; onClick: () => void}) => (
    <button
        type="button"
        data-testid={testId}
        disabled={disabled}
        onClick={onClick}
        className={`phq-call-press inline-flex h-10 items-center justify-center rounded-[10px] px-4 text-sm font-semibold disabled:opacity-40 ${tone === 'brand' ? 'bg-[var(--phq-call-brand)] text-white' : 'bg-[var(--phq-call-surface-muted)] text-[var(--phq-call-ink)]'}`}
    >
        {label}
    </button>
);
